<template>
  <div
    class="card"
    :class="[bgColor, isHovered ? 'hover' : '']"
    @mouseover="isHovered = true"
    @mouseleave="isHovered = false"
  >
    <div class="card-visual">
      <BaseImage :imageData="imageData" :editorConfig="editorConfig"></BaseImage>
    </div>
    <div
      class="flex flex-col gap-4 p-8"
      :class="{
        'text-text-light': isDarkMode,
      }"
    >
      <h4 class="font-extrabold">{{ title }}</h4>
      <p>{{ text }}</p>
      <BaseButton
        variant="secondary"
        size="small"
        :darkmode="isDarkMode"
        :text="button.text"
        :url="button.url"
      ></BaseButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import type { IBaseCard } from '@/common/components/base/BaseCard/IBaseCard'
import BaseButton from '../BaseButton/BaseButton.vue'
import BaseImage from '../BaseImage/BaseImage.vue'
import { getBgColor } from '@/common/utils/getBgColor.ts'

const isHovered = ref(false)

const props = withDefaults(defineProps<IBaseCard>(), {
  variant: 'white',
  size: 'medium',
})

const bgColor = computed(() => getBgColor(props.variant))

const isDarkMode = computed(() => props.variant == 'blue')

// Example data that would come from your API
const imageData = ref({
  image: props.image,
  caption: '',
  altText: 'Example image alt text',
  coordinates: {
    x1: 0.5,
    y1: 0.5,
    x2: 0.9,
    y2: 0.9,
  },
  // Alternative to coordinates
  focalPoint: {
    top: 0.5,
    left: 0.5,
  },
})

const editorConfig = ref({
  size: {
    width: 800,
    height: 600,
  },
})
</script>

<style scoped>
.card {
  transition:
    background-color 0.25s ease-in-out,
    border-color 0.25s ease-in-out,
    color 0.25s ease-in-out;

  @apply flex h-auto w-full flex-col items-start rounded-2xl rounded-tl-none border border-transparent text-xs shadow-md hover:cursor-pointer hover:border-border-light hover:shadow-none sm:text-sm;
}

.card:hover button {
  @apply bg-brand-02 text-text-light;
}

.card-blue:hover button {
  @apply border-accent-03 bg-accent-03 text-text-dark;
}

.card-visual {
  @apply h-auto max-h-[208px] w-full overflow-hidden rounded-tr-2xl sm:h-[314px];
}
</style>
